import type { ErrorEnvelope } from '@/generated/api/models';

/**
 * The HTTP status behind a failed request. Duck-typed on the response rather
 * than `axios.isAxiosError`, and falling back to the envelope's own
 * `statusCode`, so a rejection keeps reading the same if the transport changes.
 */
export function httpStatusOf(error: unknown): number | undefined {
  const response = (error as { response?: { status?: number; data?: ErrorEnvelope } } | null)?.response;
  return response?.status ?? response?.data?.statusCode;
}

/**
 * A permission denial — the backend's `forbidden` error kind. Branches on the
 * status, never on the message: display copy resolves from the contract, not
 * from prose (see `field-errors.ts`).
 */
export function isForbiddenError(error: unknown): boolean {
  return httpStatusOf(error) === 403;
}

/** Attempts react-query makes before giving up, for anything still retryable. */
const MAX_ATTEMPTS = 3;

/**
 * A 4xx is a decision, not a hiccup: retrying only repeats the same answer, and
 * for a denial it also floods the backend's security audit log. 5xx and
 * transport failures keep the library's default attempt budget.
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  const status = httpStatusOf(error);
  if (status !== undefined && status >= 400 && status < 500) {
    return false;
  }
  return failureCount < MAX_ATTEMPTS;
}
